import React, { useContext, useRef, useState } from 'react'
import { useParams, useRouteMatch } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Table } from 'antd'
import { useDocumentActivitiesQuery } from 'containers/VerticalPage/common/useDocumentActivitiesQuery'
import type { CurrentZone, ElasticRange } from 'utils/types'
import { selectCurrentZone } from 'store/global'
import styles from './Activity.module.scss'
import VerticalTopBarStatistics from 'UI/VerticalTopBarStatistics/VerticalTopBarStatistics'
import { activityColumns, activityExportSubHeaders, activitySubColumns, generateElasticFilters } from './utils'
import VerticalTable from 'UI/VerticalTable/VerticalTable'
import RangePicker from 'UI/RangePicker/RangePicker'
import { onActivityCSVExport } from 'utils/activities'
import ActivityExportButton from 'containers/VerticalPage/common/ActivityExportButton/ActivityExportButton'
import useTopBarStatistics from './topBarStatistics/topBarStatisticsHook'
import { getRangePickerStringValues } from './utils'
import { COLUMNS_IDS } from 'UI/VerticalTable/utils'
import Income from 'components/Income'
import { VerticalContext } from 'store/verticals/Context'
import Filters from './Filters/Filters'
import useFilterOptions from './useFilterOptions'
import useDateRangeValues from './useDateRangeValues'
import { useTableScroll } from './useTableScroll'
import { useWindowSize } from 'hooks/useWindowSize'
import { VerticalDocumentActivity } from 'store/verticals/types'

const exportIconUrl = require('icons/export.svg')
const PAGE_SIZE = 100

const Activity = () => {
  const { verticalEntity } = useContext(VerticalContext)
  const { height: windowHeight } = useWindowSize()

  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const [selectedDate, setSelectedDate] = useState<{ start: string; end: string } | null>(null)

  const [filtersData, setFiltersData] = useState<Record<string, string>[]>([])

  const [filterRange, setFilterRange] = useState<ElasticRange | undefined>()

  const { data: topBarStatisticsData, isLoading: topBarStatisticsIsLoading } = useTopBarStatistics({
    period: selectedDate,
    filters: filtersData,
    range: filterRange,
  })

  const currentZone: CurrentZone = useSelector(selectCurrentZone)
  const { entityId } = useParams<{ entityId: string }>()

  const {
    verticalDocumentActivity: activities,
    total: totalActivities,
    isFetching: activitiesIsFetching,
    fetchNextPage: fetchMoreActivities,
  } = useDocumentActivitiesQuery({
    entityId,
    detailsFilters: filtersData,
    zoneActivityIndices: currentZone.cryptoActivityIndices,
    currentPeriod: selectedDate,
    pageSize: PAGE_SIZE,
    activityType: undefined,
    sort: [{ activityDate: 'desc' }],
    additionalDataAddToRow: verticalEntity?.entityDetails,
    filterRange,
  })

  useTableScroll(activities)

  const { options: filterOptions } = useFilterOptions()
  const { yearMonthRange: defaultDatePickerRange } = useDateRangeValues()

  const { params: { vertical = '' } = {} } = useRouteMatch<{ vertical: string }>('/vertical/:vertical') || {}

  const containerRef = useRef<HTMLDivElement>(null)
  const statisticsRef = useRef<HTMLDivElement>(null)

  const handleExport = () =>
    onActivityCSVExport(
      currentZone.cryptoActivityIndices,
      entityId,
      vertical,
      JSON.stringify(filtersData),
      [{ activityDate: 'desc' }],
      undefined,
      'CryptoActivity',
      activityExportSubHeaders,
    )

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={styles.filtersContainer}>
        {filterOptions && (
          <Filters
            onSubmit={data => {
              if (data) {
                const elasticFiltersData = generateElasticFilters(data)
                setFiltersData(elasticFiltersData.filters)
                setFilterRange(elasticFiltersData.range)
              } else {
                setFiltersData([])
                setFilterRange(undefined)
              }
            }}
            options={filterOptions}
          >
            {(filtersData.length > 0 || filterRange) && (
              <button
                className={styles.reset}
                onClick={() => {
                  setFiltersData([])
                  setFilterRange(undefined)
                }}
              >
                Clear Filters
              </button>
            )}
          </Filters>
        )}
        {defaultDatePickerRange && (
          <RangePicker
            picker='month'
            style={{ width: 310 }}
            className={styles.rangePicker}
            submitText='Less than a full tax year selected: Presenting only sales tax/vat data and no income tax'
            placeholder={[defaultDatePickerRange?.minDate, defaultDatePickerRange?.maxDate]}
            onSubmit={dateValue => setSelectedDate(getRangePickerStringValues(dateValue))}
          />
        )}
      </div>
      {!topBarStatisticsIsLoading && topBarStatisticsData && (
        <div ref={statisticsRef}>
          <VerticalTopBarStatistics data={topBarStatisticsData} />
        </div>
      )}
      <VerticalTable
        pageSize={PAGE_SIZE}
        enableColumnPicker
        scroll={{ x: 'max-content', y: windowHeight - 120 }}
        total={totalActivities}
        columns={activityColumns}
        removeFromPickerColumns={[COLUMNS_IDS.cryptoTokenName]}
        disabledColumns={[COLUMNS_IDS.cryptoAmount, COLUMNS_IDS.cryptoTokenType]}
        dataSource={activities || []}
        rowClassName={record => {
          if (expandedRowKeys.length > 0) {
            if (!expandedRowKeys.includes(record.id)) return styles.blurredRow
            return styles.expandedRow
          }
          return styles.defaultRowClassName
        }}
        expandable={{
          expandedRowRender: record => {
            return (
              <div className={styles.subRowContainer}>
                <div className={styles.title}>Cost Basis Details:</div>
                <VerticalTable
                  columns={activitySubColumns}
                  dataSource={
                    record?.subActivities?.map((subActivity: VerticalDocumentActivity, index: number) => ({
                      ...subActivity,
                      id: `${record.id}-sub-${index}`,
                    })) || []
                  }
                  pageSize={Infinity}
                  summary={pageData => {
                    let totalCostBasis = 0
                    let totalPurchaseValue = 0

                    pageData.forEach(({ costBasis, value }) => {
                      totalCostBasis = totalCostBasis + (costBasis || 0)
                      totalPurchaseValue = totalPurchaseValue + (value || 0)
                    })

                    return (
                      <Table.Summary.Row>
                        {[...new Array(7)].map((_, i) => (
                          <Table.Summary.Cell key={i} index={i}></Table.Summary.Cell>
                        ))}
                        <Table.Summary.Cell key={7} index={7} className={styles.summaryValue}>
                          TOTAL:
                        </Table.Summary.Cell>
                        <Table.Summary.Cell key={8} index={8} className={styles.summaryValue}>
                          <Income value={totalCostBasis} />
                        </Table.Summary.Cell>
                        <Table.Summary.Cell key={9} index={9} className={styles.summaryValue}>
                          <Income value={totalPurchaseValue} />
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )
                  }}
                />
              </div>
            )
          },
          onExpand: (expanded, record) => {
            if (expanded) {
              setExpandedRowKeys([record.id])
            } else setExpandedRowKeys([])
          },
          expandedRowKeys: expandedRowKeys,
          rowExpandable: record => (record?.subActivities?.length ?? 0) > 0,
        }}
        fetchNextPage={fetchMoreActivities}
        loading={activitiesIsFetching}
      >
        <div className={styles.exportButtonWrapper}>
          <ActivityExportButton onExport={handleExport} icon={<img src={exportIconUrl} alt='Export' />}>
            Export
          </ActivityExportButton>
        </div>
      </VerticalTable>
    </div>
  )
}

export default Activity
